import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type PromoCodeInput, type Product } from '../api.js';
import { useActiveProfileId } from '../active-profile.js';
import { Icon } from '../components/Icon.js';
import { Switch } from '../components/ui.js';
import { Topbar } from './AdminLayout.js';
import { addDays, endOfIsraelDay, israelDateOf, israelToday } from '../utils/israel-time.js';

interface Form {
  code: string;
  description: string;
  discountType: 'percent' | 'fixed';
  // только для percent
  percentValue: number | '';
  // только для fixed (хотя бы одна > 0)
  fixedIls: number | '';
  fixedRub: number | '';
  appliesToAllProducts: boolean;
  productIds: string[];
  maxUses: number | '';
  expiresAt: string; // YYYY-MM-DD или ''
  isActive: boolean;
}

const EMPTY: Form = {
  code: '',
  description: '',
  discountType: 'percent',
  percentValue: 10,
  fixedIls: '',
  fixedRub: '',
  appliesToAllProducts: false,
  productIds: [],
  maxUses: '',
  expiresAt: '',
  isActive: true,
};

// Срок действия — до конца выбранного дня по Израилю (23:59:59), с какого
// бы компьютера ни сохраняли. Раньше брался пояс браузера.
function toIsoOrNull(date: string): string | null {
  if (!date) return null;
  return endOfIsraelDay(date);
}

function fromIsoToDate(iso: string | null): string {
  if (!iso) return '';
  return israelDateOf(iso);
}

// Нормализация кода: верхний регистр, без пробелов и невидимых символов.
function normalizeCode(raw: string): string {
  return raw.replace(/\s+/g, '').toUpperCase();
}

type ErrorField = 'code' | 'discount' | 'apply' | 'general';

interface FieldError {
  field: ErrorField;
  message: string;
}

// API возвращает технические коды ошибок — переводим в понятный русский UI
// + указываем в каком блоке формы показывать ошибку.
const ERROR_MAP: Record<string, FieldError> = {
  must_pick_at_least_one_product_or_apply_to_all: {
    field: 'apply',
    message: 'Выберите хотя бы один продукт или примените ко всем продуктам.',
  },
  product_ids_invalid_or_foreign: {
    field: 'apply',
    message: 'Один из выбранных продуктов не принадлежит активному профилю.',
  },
  at_least_one_fixed_currency_required: {
    field: 'discount',
    message: 'Укажите сумму скидки хотя бы в одной валюте (ILS или RUB).',
  },
  percent_value_required: { field: 'discount', message: 'Укажите процент скидки.' },
  percent_value_required_1_to_100: {
    field: 'discount',
    message: 'Процент скидки должен быть от 1 до 100.',
  },
  fixed_must_be_positive: { field: 'discount', message: 'Сумма скидки должна быть больше нуля.' },
  code_already_exists: {
    field: 'code',
    message: 'Такой код уже существует в этом профиле.',
  },
  no_active_profile: { field: 'general', message: 'Не выбран активный профиль.' },
  invalid_body: { field: 'general', message: 'Не все поля заполнены корректно.' },
  create_failed: { field: 'general', message: 'Не удалось сохранить промокод.' },
  update_failed: { field: 'general', message: 'Не удалось сохранить изменения.' },
};

function parseError(raw: string): FieldError {
  // raw может прийти как JSON ({error,fieldErrors}) от zod или как plain message.
  try {
    const parsed = JSON.parse(raw) as { error?: string; fieldErrors?: Record<string, string[]> };
    if (parsed.error) {
      const mapped = ERROR_MAP[parsed.error];
      if (mapped) return mapped;
    }
    if (parsed.fieldErrors) {
      const first = Object.values(parsed.fieldErrors).flat()[0];
      if (first) {
        return ERROR_MAP[first] ?? { field: 'general', message: first };
      }
    }
  } catch {
    // не JSON
  }
  return ERROR_MAP[raw] ?? { field: 'general', message: raw };
}

/**
 * Скопировать код, когда сохранение вернёт его. true — код в буфере.
 * ClipboardItem с обещанием — единственный способ, который Safari пропускает
 * после сетевого запроса; где его нет, пробуем обычный writeText.
 */
function copyWhenReady(code: Promise<string>): Promise<boolean> {
  try {
    if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      return navigator.clipboard
        .write([new ClipboardItem({ 'text/plain': code.then((c) => new Blob([c], { type: 'text/plain' })) })])
        .then(() => true)
        .catch(() => false);
    }
  } catch {
    // ClipboardItem без поддержки обещаний — ниже обычный путь.
  }
  return code
    .then((c) => navigator.clipboard?.writeText(c))
    .then(() => true)
    .catch(() => false);
}

export function PromoCodeEdit(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const nav = useNavigate();
  const [form, setForm] = useState<Form>(EMPTY);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<FieldError | null>(null);
  const [codeCopied, setCodeCopied] = useState(false);
  const [activeProfileId] = useActiveProfileId();

  const update = <K extends keyof Form>(key: K, value: Form[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  useEffect(() => {
    void api.listProducts().then(setProducts).catch(() => undefined);
  }, [activeProfileId]);

  useEffect(() => {
    if (isNew) return;
    void api.getPromoCode(id!).then((p) => {
      setForm({
        code: p.code,
        description: p.description,
        discountType: p.discountType,
        percentValue: p.percentValue ?? '',
        fixedIls: p.fixedIls ?? '',
        fixedRub: p.fixedRub ?? '',
        appliesToAllProducts: p.appliesToAllProducts,
        productIds: p.productIds,
        maxUses: p.maxUses ?? '',
        expiresAt: fromIsoToDate(p.expiresAt),
        isActive: p.isActive,
      });
      setLoading(false);
    });
  }, [id, isNew]);

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    // Буфер обмена нужно занять ещё в обработчике нажатия: после запроса к
    // серверу Safari писать в буфер уже не даёт. Поэтому запись начинается
    // сразу, а текст в неё попадёт, когда сохранение пройдёт.
    let resolveSaved: (code: string) => void = () => undefined;
    let rejectSaved: () => void = () => undefined;
    const savedCode = new Promise<string>((res, rej) => {
      resolveSaved = res;
      rejectSaved = rej;
    });
    const copied = copyWhenReady(savedCode);
    try {
      const body: PromoCodeInput = {
        code: form.code.trim(),
        description: form.description,
        discountType: form.discountType,
        percentValue: form.discountType === 'percent' ? Number(form.percentValue) : null,
        fixedIls:
          form.discountType === 'fixed' && form.fixedIls !== '' ? Number(form.fixedIls) : null,
        fixedRub:
          form.discountType === 'fixed' && form.fixedRub !== '' ? Number(form.fixedRub) : null,
        appliesToAllProducts: form.appliesToAllProducts,
        productIds: form.appliesToAllProducts ? [] : form.productIds,
        maxUses: form.maxUses === '' ? null : Number(form.maxUses),
        expiresAt: toIsoOrNull(form.expiresAt),
        isActive: form.isActive,
      };
      const saved = isNew ? await api.createPromoCode(body) : await api.updatePromoCode(id!, body);
      resolveSaved(saved.code);
      // Сохранили — закрываем страницу; список покажет, что сохранилось и скопировано ли.
      nav('/admin/promo-codes', {
        state: {
          savedPromo: {
            code: saved.code,
            created: isNew,
            // Буфер не ответил за 1,5 с (запрос разрешения и т. п.) — не ждём и не обещаем копию.
            copied: await Promise.race([copied, new Promise<boolean>((r) => setTimeout(() => r(false), 1500))]),
          },
        },
      });
    } catch (e) {
      rejectSaved();
      const details = (e as { details?: unknown }).details;
      const raw = typeof details === 'string' ? details : JSON.stringify(details ?? {});
      setErr(parseError(raw));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (): Promise<void> => {
    if (!id || isNew) return;
    if (!confirm('Удалить промокод? Эта операция необратима.')) return;
    await api.deletePromoCode(id);
    nav('/admin/promo-codes');
  };

  const toggleProduct = (productId: string): void => {
    setForm((f) => {
      const has = f.productIds.includes(productId);
      return {
        ...f,
        productIds: has ? f.productIds.filter((x) => x !== productId) : [...f.productIds, productId],
      };
    });
  };

  if (loading) {
    return (
      <>
        <Topbar title="Промокод" />
        <div style={{ padding: 24, color: 'var(--fg-subtle)' }}>Загрузка…</div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={isNew ? 'Новый промокод' : form.code || 'Промокод'}
        actions={
          isNew ? undefined : (
            <button type="button" className="btn btn-sm btn-ghost" onClick={onDelete}>
              <Icon name="trash" size={13} />
              Удалить
            </button>
          )
        }
      />
      <div style={{ padding: '24px 28px', flex: 1, overflowY: 'auto' }}>
        <form onSubmit={onSubmit}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 380px',
              gap: 20,
              maxWidth: 1080,
            }}
          >
            {/* ============== LEFT COLUMN ============== */}
            <div className="stack">
              {/* Основное */}
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Основное</div>
                </div>
                <div className="card-body stack">
                  <div className="field">
                    <label className="field-label">Код *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="input mono"
                        value={form.code}
                        onChange={(e) => update('code', normalizeCode(e.target.value))}
                        onBlur={(e) => update('code', normalizeCode(e.target.value))}
                        required
                        maxLength={64}
                        placeholder="EARLY30"
                        style={{ textTransform: 'uppercase', paddingRight: 40 }}
                      />
                      <button
                        type="button"
                        title={codeCopied ? 'Скопировано' : 'Скопировать код'}
                        onClick={() => {
                          if (!form.code) return;
                          navigator.clipboard?.writeText(form.code).catch(() => undefined);
                          setCodeCopied(true);
                          setTimeout(() => setCodeCopied(false), 1400);
                        }}
                        style={{
                          position: 'absolute',
                          right: 6,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          width: 30,
                          height: 30,
                          borderRadius: 'var(--radius)',
                          border: 'none',
                          background: 'transparent',
                          color: codeCopied ? 'var(--success-fg, #16a34a)' : 'var(--fg-muted)',
                          cursor: form.code ? 'pointer' : 'default',
                          display: 'grid',
                          placeItems: 'center',
                          padding: 0,
                          opacity: form.code ? 1 : 0.4,
                        }}
                        disabled={!form.code}
                      >
                        <Icon name={codeCopied ? 'check' : 'copy'} size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Описание</label>
                    <input
                      type="text"
                      className="input"
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                      maxLength={2000}
                      placeholder="Например: «раннее бронирование»"
                    />
                  </div>
                  {err && err.field === 'code' && (
                    <div style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{err.message}</div>
                  )}
                </div>
              </div>

              {/* Скидка */}
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Скидка</div>
                </div>
                <div className="card-body stack">
                  <div className="field">
                    <label className="field-label">Тип</label>
                    <select
                      className="input"
                      value={form.discountType}
                      onChange={(e) =>
                        update('discountType', e.target.value as 'percent' | 'fixed')
                      }
                    >
                      <option value="percent">Процент</option>
                      <option value="fixed">Фиксированная сумма</option>
                    </select>
                  </div>

                  {form.discountType === 'percent' ? (
                    <div className="field">
                      <label className="field-label">Процент скидки</label>
                      <div className="field-prefix">
                        <span className="prefix-txt">%</span>
                        <input
                          type="number"
                          className="input no-spinner"
                          min={1}
                          max={100}
                          value={form.percentValue}
                          onChange={(e) =>
                            update('percentValue', e.target.value === '' ? '' : Number(e.target.value))
                          }
                          required
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="grid-2">
                        <div className="field">
                          <label className="field-label">Скидка в ILS</label>
                          <div className="field-prefix">
                            <span className="prefix-txt">₪</span>
                            <input
                              type="number"
                              className="input no-spinner"
                              min={0}
                              value={form.fixedIls === '' ? '' : (Number(form.fixedIls) / 100).toString()}
                              onChange={(e) => {
                                const major = e.target.value;
                                if (major === '') {
                                  update('fixedIls', '');
                                } else {
                                  // храним в minor units
                                  update('fixedIls', Math.round(parseFloat(major) * 100));
                                }
                              }}
                              step="0.01"
                              placeholder="пусто = не применяется"
                            />
                          </div>
                        </div>
                        <div className="field">
                          <label className="field-label">Скидка в RUB</label>
                          <div className="field-prefix">
                            <span className="prefix-txt">₽</span>
                            <input
                              type="number"
                              className="input no-spinner"
                              min={0}
                              value={form.fixedRub === '' ? '' : (Number(form.fixedRub) / 100).toString()}
                              onChange={(e) => {
                                const major = e.target.value;
                                if (major === '') {
                                  update('fixedRub', '');
                                } else {
                                  update('fixedRub', Math.round(parseFloat(major) * 100));
                                }
                              }}
                              step="0.01"
                              placeholder="пусто = не применяется"
                            />
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        Заполни хотя бы одну валюту. Если оставлены обе — промо работает для обеих
                        валют. Если цена меньше скидки — скидка не уйдёт в минус.
                      </div>
                    </>
                  )}
                  {err && err.field === 'discount' && (
                    <div style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{err.message}</div>
                  )}
                </div>
              </div>

              {/* Применить */}
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Применить</div>
                </div>
                <div className="card-body stack">
                  <label
                    className="field"
                    style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}
                  >
                    <input
                      type="radio"
                      name="scope"
                      checked={form.appliesToAllProducts}
                      onChange={() => update('appliesToAllProducts', true)}
                    />
                    <span>Все продукты</span>
                  </label>
                  <label
                    className="field"
                    style={{ display: 'flex', flexDirection: 'row', gap: 8, alignItems: 'center' }}
                  >
                    <input
                      type="radio"
                      name="scope"
                      checked={!form.appliesToAllProducts}
                      onChange={() => update('appliesToAllProducts', false)}
                    />
                    <span>Выборочно</span>
                  </label>

                  {!form.appliesToAllProducts && (
                    <div
                      style={{
                        border: '1px solid var(--hairline)',
                        borderRadius: 'var(--radius)',
                        padding: 12,
                        maxHeight: 260,
                        overflowY: 'auto',
                      }}
                    >
                      {products.length === 0 ? (
                        <div style={{ color: 'var(--fg-subtle)', fontSize: 13 }}>
                          Нет продуктов в этом профиле.
                        </div>
                      ) : (
                        products.map((p) => (
                          <label
                            key={p.id}
                            style={{
                              display: 'flex',
                              gap: 10,
                              alignItems: 'center',
                              padding: '6px 4px',
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={form.productIds.includes(p.id)}
                              onChange={() => toggleProduct(p.id)}
                            />
                            <span style={{ flex: 1 }}>
                              <bdi>{p.name}</bdi>{' '}
                              <span
                                style={{
                                  color: 'var(--fg-subtle)',
                                  fontFamily: 'var(--font-mono)',
                                  fontSize: 12,
                                }}
                              >
                                {p.slug}
                              </span>
                            </span>
                            <span
                              style={{
                                color: 'var(--fg-muted)',
                                fontSize: 12,
                                fontFamily: 'var(--font-mono)',
                              }}
                            >
                              {p.priceIls > 0 ? `${p.priceIls / 100} ₪` : ''}
                              {p.priceIls > 0 && p.priceRub > 0 ? ' / ' : ''}
                              {p.priceRub > 0 ? `${p.priceRub / 100} ₽` : ''}
                            </span>
                          </label>
                        ))
                      )}
                    </div>
                  )}
                  {err && err.field === 'apply' && (
                    <div style={{ color: 'var(--danger-fg)', fontSize: 13, marginTop: 4 }}>
                      {err.message}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* ============== RIGHT COLUMN ============== */}
            <div className="stack">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">Публикация</div>
                </div>
                <div className="card-body stack">
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>Активен</div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        Принимает применения
                      </div>
                    </div>
                    <Switch checked={form.isActive} onChange={(v) => update('isActive', v)} />
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>Ограничить число использований</div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        Деактивируется после N применений
                      </div>
                    </div>
                    <Switch
                      checked={form.maxUses !== ''}
                      onChange={(v) =>
                        update('maxUses', v ? (form.maxUses === '' ? 10 : form.maxUses) : '')
                      }
                    />
                  </div>
                  {form.maxUses !== '' && (
                    <div className="field">
                      <label className="field-label">Максимум применений</label>
                      <input
                        className="input no-spinner"
                        type="number"
                        inputMode="numeric"
                        min={1}
                        step="1"
                        value={form.maxUses}
                        onChange={(e) => {
                          const n = Math.max(1, Math.floor(+e.target.value || 0));
                          update('maxUses', n);
                        }}
                      />
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>Срок действия</div>
                      <div style={{ fontSize: 12, color: 'var(--fg-subtle)' }}>
                        Перестанет работать после даты
                      </div>
                    </div>
                    <Switch
                      checked={form.expiresAt !== ''}
                      onChange={(v) => {
                        if (!v) {
                          update('expiresAt', '');
                        } else {
                          // дефолт — через 30 дней от сегодняшней даты в Израиле
                          update('expiresAt', addDays(israelToday(), 30));
                        }
                      }}
                    />
                  </div>
                  {form.expiresAt !== '' && (
                    <div className="field">
                      <label className="field-label">Действует до</label>
                      <input
                        type="date"
                        className="input"
                        value={form.expiresAt}
                        onChange={(e) => update('expiresAt', e.target.value)}
                      />
                      <div className="field-hint">
                        Включительно: код работает весь этот день, до 23:59 по Израилю.
                      </div>
                    </div>
                  )}

                  <hr className="hr" />

                  {/* Fallback для общих ошибок (general). Field-level ошибки
                      рендерятся в своих блоках в левой колонке. */}
                  {err && err.field === 'general' && (
                    <div style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{err.message}</div>
                  )}

                  <button
                    type="submit"
                    disabled={busy}
                    className="btn btn-primary"
                    style={{ justifyContent: 'center', width: '100%' }}
                  >
                    <Icon name="check" size={14} />
                    {busy ? 'Сохраняем…' : 'Сохранить'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ justifyContent: 'center', width: '100%' }}
                    onClick={() => nav('/admin/promo-codes')}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}
